import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { TagGeneral } from './TagGeneral';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';

export type TagOverflowItem = {
  key: string;
  /** The rendered tag chip (e.g. a TagColored). */
  tag: ReactNode;
  /** Text representing this item in the "+N" overflow tooltip. */
  tooltipText: string;
};

export type TagOverflowRowProps = {
  items: TagOverflowItem[];
  /** Gap between tags in px (must be numeric for the fit calculation). */
  gap?: number;
  className?: string;
};

/**
 * Single-line row of tags that shows as many as fit the available width and
 * collapses the rest into a "+N" TagGeneral with a tooltip listing the
 * hidden items. At least one tag is always shown when items exist.
 *
 * Fit is computed from a hidden measurement layer that always renders every
 * tag, so the row adapts when the container grows or shrinks.
 */
export function TagOverflowRow({ items, gap = 8, className }: TagOverflowRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const updateVisibleCount = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const available = container.clientWidth;
    if (available <= 0) return;

    const children = Array.from(measure.children) as HTMLElement[];
    // The last measurement child is the worst-case "+N" overflow tag.
    const overflowWidth = children[children.length - 1]?.offsetWidth ?? 0;
    const tagWidths = children.slice(0, -1).map((el) => el.offsetWidth);

    // All tags fit without an overflow indicator.
    const totalAll = tagWidths.reduce((sum, w, i) => sum + w + (i > 0 ? gap : 0), 0);
    if (totalAll <= available) {
      setVisibleCount(tagWidths.length);
      return;
    }

    // Reserve room for the "+N" tag, then fit as many tags as possible.
    // With `count` tags visible the row is: tags + gaps + overflow tag,
    // i.e. Σwidths + count * gap + overflowWidth.
    let used = overflowWidth;
    let count = 0;
    for (const width of tagWidths) {
      if (used + width + gap > available) break;
      used += width + gap;
      count += 1;
    }
    setVisibleCount(Math.max(count, tagWidths.length > 0 ? 1 : 0));
  }, [gap]);

  useLayoutEffect(() => {
    updateVisibleCount();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateVisibleCount);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [items, updateVisibleCount]);

  const visibleItems = items.slice(0, visibleCount);
  const hiddenItems = items.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap,
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      {visibleItems.map((item) => (
        <span key={item.key} style={{ display: 'inline-flex', flexShrink: 0 }}>
          {item.tag}
        </span>
      ))}
      {hiddenItems.length > 0 && (
        <TooltipTrigger text={hiddenItems.map((item) => item.tooltipText).join(', ')}>
          <span style={{ display: 'inline-flex', flexShrink: 0 }}>
            <TagGeneral label={`+${hiddenItems.length}`} />
          </span>
        </TooltipTrigger>
      )}
      {/* Hidden measurement layer: every tag plus a worst-case "+N" tag. */}
      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          visibility: 'hidden',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          zIndex: -1,
        }}
      >
        {items.map((item) => (
          <span key={item.key} style={{ display: 'inline-flex', flexShrink: 0 }}>
            {item.tag}
          </span>
        ))}
        <span style={{ display: 'inline-flex', flexShrink: 0 }}>
          <TagGeneral label={`+${items.length}`} />
        </span>
      </div>
    </div>
  );
}
